import S, { DataSignal } from "s-js";
import { debounce } from "src-lib/util/async";
import * as Surplus from "surplus";
import "./_ToolTip.scss";

const activeTooltips = new Map<string, Function>();

/** Two styles. One, include children and the tooltip will wrap around it.
 * Two, include datasignals that trigger when the mouse enters and when the mouse leaves the component being wrapped by hte tool tip*/
type TooltipProps = {
    label: string | JSX.Children;
    margin?: { top?: number; right?: number; down?: number; left?: number };
    minWidth?: number;
    maxWidth?: number;
    group?: string;
    enterDelay?: number;
    leaveDelay?: number;
    children?: any;
    event?: {
        onMouseEnter: DataSignal<HTMLElement | undefined>;
        onMouseLeave: DataSignal<HTMLElement | undefined>;
    };
};

type ParentTooltipProps = {
    mouseStart: { x: number; y: number };
};
let divGlobalTooltipContainer: HTMLElement | undefined;

export function Tooltip(props: TooltipProps): JSX.Element {
    // create global tooltip container. Gets around overflow issues, like portals
    if (!divGlobalTooltipContainer) {
        divGlobalTooltipContainer = document.createElement("div");
        document.body.appendChild(divGlobalTooltipContainer);
    }
    const isLazy = S.data(true);
    let mouseStart = { x: -500, y: -500 };
    // return a simple wrapper at first. But only if we aren't using special event props
    return S(() =>
        isLazy() && !props.event ? (
            <LazyTooltip onMouseEnter={onMouseEnter}>{props.children}</LazyTooltip>
        ) : (
            <FullTooltip {...props} mouseStart={mouseStart}>
                {props.children}
            </FullTooltip>
        ),
    ) as any;

    function onMouseEnter(e: MouseEvent) {
        mouseStart.x = e.clientX;
        mouseStart.y = e.clientY;
        isLazy(false);
    }
}

function FullTooltip(props: TooltipProps & ParentTooltipProps) {
    const group = props?.group ?? "common-tooltip",
        enterDelay = props?.enterDelay ?? 30,
        leaveDelay = props?.leaveDelay ?? 450;

    let isShown = true,
        isOpen = false,
        activeCloseTooltip: ReturnType<typeof debounce> | undefined,
        activeDetect: ReturnType<typeof debounce> | undefined,
        lastMouseX = props.mouseStart.x,
        lastMouseY = props.mouseStart.y;

    const domTooltip = renderTooltip();

    window.requestAnimationFrame(() => {
        onMouseEnter();
    });
    const prEv = props.event;
    let domContainer: HTMLElement | undefined;
    if (!prEv) {
        domContainer = (
            <div class="tooltipContainer">
                <div
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                    onClick={handleOnClick}>
                    {props.children}
                </div>
            </div>
        );
        return domContainer;
    } else {
        // props.event allow mouse enter/leave events to be triggered externally.
        // Mainly for event delegation
        S.on(prEv.onMouseEnter, () => {
            const e = prEv.onMouseEnter();
            if (!e) return;
            domContainer = e;
            // console.log("      mouseenter", e);

            onMouseEnter();
        });

        S.on(prEv.onMouseLeave, () => {
            const e = prEv.onMouseLeave();
            if (!e) return;
            domContainer = e;
            // console.log("      mouseleave", e);

            onMouseLeave();
        });

        return null;
    }

    function handleOnClick(e: MouseEvent) {
        e.stopPropagation();
        tryActivateTooltip(-500, -500);
    }

    function renderTooltip() {
        const domTooltip = (
            <div
                class="tooltipContentContainer"
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
                style={{
                    width: props.maxWidth ? `${props.maxWidth}px` : "",
                    minWidth: props.minWidth ? `${props.minWidth}px` : "",
                }}>
                <div>{props.label}</div>
            </div>
        );
        domTooltip.style.opacity = "0";
        domTooltip.style.display = "none";
        domTooltip.style.transition = "";
        divGlobalTooltipContainer?.appendChild(domTooltip);
        return domTooltip;
    }

    function handleOnMouseEnter(e: MouseEvent) {
        e.stopPropagation();
        onMouseEnter();
    }

    function handleOnMouseMove(e: MouseEvent) {
        // Allow event to propagate to outer elements
        tryActivateTooltip(e.clientX, e.clientY);
    }

    function handleOnMouseLeave(e: MouseEvent) {
        e.stopPropagation();
        onMouseLeave();
    }

    function onMouseLeave() {
        isShown = false;
        if (activeDetect) {
            activeDetect.cancel();
            activeDetect = undefined;
        }
        lastMouseX = -500;
        lastMouseY = -500;
        domContainer?.removeEventListener("mousemove", handleOnMouseMove);
        domTooltip.removeEventListener("mousemove", handleOnMouseMove);

        activeCloseTooltip = debounce(leaveDelay, closeTooltip);
        // console.log("handleOnMouseLeave", props.label);
    }

    function onMouseEnter() {
        const activeTooltip = activeTooltips.get(group);
        if (activeTooltip && !isShown) {
            // console.log("KILL");
            if (activeTooltip !== closeTooltip) activeTooltip();
        }
        isShown = true;

        if (activeCloseTooltip) {
            activeCloseTooltip.cancel();
            activeCloseTooltip = undefined;
            setTooltipPosition();
        }
        divGlobalTooltipContainer?.appendChild(domTooltip);
        domContainer?.addEventListener("mousemove", handleOnMouseMove);
        domTooltip.addEventListener("mousemove", handleOnMouseMove);

        // console.log("handleOnMouseEnter", props.label);
    }

    function tryActivateTooltip(clientX: number, clientY: number) {
        if (Math.abs(lastMouseX - clientX) + Math.abs(lastMouseY - clientY) > 4) {
            if (activeDetect) activeDetect.cancel();
            activeDetect = debounce(enterDelay, openTooltip);
            lastMouseX = clientX;
            lastMouseY = clientY;
        }
    }

    function setTooltipPosition() {
        const bodyRect = document.body.getBoundingClientRect();
        const bboxViewport = domContainer?.getBoundingClientRect() ?? {
                left: 0,
                width: 1,
                height: 1,
                top: 0,
                bottom: 1,
            },
            bboxTooltip = domTooltip.firstElementChild!.getBoundingClientRect();
        const PADDING = 4;
        const xMiddle = Math.round(bboxViewport.width / 2);

        const left = bboxViewport.left + xMiddle;
        const xo =
                left - Math.round(bboxTooltip.width / 2) < PADDING
                    ? Math.abs(left - Math.round(bboxTooltip.width / 2)) + PADDING
                    : left + bboxTooltip.width / 2 - PADDING > document.body.clientWidth
                    ? -1 * (left + bboxTooltip.width / 2 - document.body.clientWidth + PADDING)
                    : 0,
            yo =
                bboxViewport.top < PADDING
                    ? Math.abs(bboxViewport.top) + PADDING
                    : bboxViewport.bottom - PADDING > document.body.clientHeight
                    ? -1 * (bboxViewport.bottom - document.body.clientHeight + PADDING)
                    : 0;

        domTooltip.style.left = `${
            (props?.margin?.left ?? 0) -
            (props?.margin?.right ?? 0) +
            bboxViewport.left +
            Math.round(xMiddle) -
            bodyRect.left +
            xo
        }px`;
        domTooltip.style.top = `${
            (props?.margin?.top ?? 0) -
            (props?.margin?.right ?? 0) +
            bboxViewport.top -
            bodyRect.top +
            yo
        }px`;
    }

    function openTooltip() {
        if (isShown && isOpen) return;
        isOpen = true;
        // console.log("openTooltip");
        activeTooltips.set(group, closeTooltip);
        activeDetect = undefined;
        domTooltip.clientHeight;

        window.requestAnimationFrame(() => {
            domTooltip.style.transition = "all 0s";
            domTooltip.style.display = "flex";
            domTooltip.style.opacity = "1";
            domTooltip.style.transform = "scale(1) translate(-50%, -110%)";

            domTooltip.clientHeight;
            window.requestAnimationFrame(() => {
                setTooltipPosition();
                domTooltip.style.transition = "all 0.02s  linear";
                domTooltip.clientHeight;

                const onTransitionEnd = (e: TransitionEvent) => {
                    window.removeEventListener("transitionend", onTransitionEnd);
                    domTooltip.style.transform = "";
                    domTooltip.style.filter = "";
                };

                domTooltip.addEventListener("transitionend", onTransitionEnd);

                window.requestAnimationFrame(() => {
                    domTooltip.style.transform = "scale(1.0) translate(-50%, -110%)";
                    domTooltip.style.opacity = "1";
                    domTooltip.clientHeight;
                });
            });
        });
    }

    function closeTooltip() {
        if (!isShown && !isOpen) return;
        isOpen = false;
        // console.log("transitionOff", props.label);
        if (activeCloseTooltip) activeCloseTooltip.cancel();
        activeCloseTooltip = undefined;
        if (activeDetect) activeDetect.cancel();
        activeTooltips.delete(group);
        domTooltip.style.transition = "all .15s cubic-bezier(.17,.67,.83,.67)";

        window.addEventListener("transitionend", onTransitionEnd);
        window.requestAnimationFrame(() => {
            domTooltip.style.opacity = "0";
            domTooltip.style.filter = "grayscale(.3)";
        });
    }

    function onTransitionEnd(e: TransitionEvent) {
        if (isShown) return;
        domTooltip.style.opacity = "0";
        domTooltip.style.filter = "";
        domTooltip.style.display = "none";
        domTooltip.style.transition = "";
        domTooltip.style.top = "";
        domTooltip.style.left = "";
        window.removeEventListener("transitionend", onTransitionEnd);
        divGlobalTooltipContainer?.removeChild(domTooltip);
    }
}

/** Simple tooltip  That defers creating dom element until needed*/
function LazyTooltip(props: { onMouseEnter: (e: MouseEvent) => void; children: any }) {
    return (
        <div class="tooltipContainer" onMouseEnter={props.onMouseEnter}>
            {props.children}
        </div>
    );
}
